import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { GapBox } from 'components/Box'
import { BoxForm, RadioGroup, Switch, TextField } from 'components/Form'
import { SectionHeader } from 'components/Header'
import Paper from 'components/Paper'
import { useMatchTypeRadioGroup } from 'features/inventory/page/forms/GeneralForm/hooks/useMatchTypeRadioGroup'
import { useAbility } from 'providers/casl'
import { CONTAINER_SPACING } from 'providers/material-ui/theme/constants'

import type { Schema } from './schema'
import type { TooltipProps } from 'components/Tooltip'

export const TriggersSection = (): React.JSX.Element => {
  const { t } = useTranslation('features/domain', {
    keyPrefix: 'form.richPlacements.kWords.triggers',
  })
  const { control, watch } = useFormContext<Schema>()
  const ability = useAbility()

  const matchTypeTooltip: TooltipProps = {
    content: [
      t('matchType.tooltip.line1'),
      t('matchType.tooltip.line2'),
      t('matchType.tooltip.line3'),
    ],
  }

  const matchTypeOptions = useMatchTypeRadioGroup()

  const isDisabled: boolean = ability.cannot('update', 'Domain')

  const { customVariableTriggerEnabled, pathTriggerEnabled } = watch()

  return (
    <Paper data-cy='section-triggers'>
      <SectionHeader
        subtitle={t('subtitle')}
        title={t('title')}
      />

      <GapBox />

      <BoxForm>
        <Switch
          control={control}
          disabled={isDisabled}
          label={t('pathTriggerEnabled.label')}
          labelPlacement='start'
          name='pathTriggerEnabled'
          tooltip={{
            content: t('pathTriggerEnabled.tooltip'),
          }}
        />

        {pathTriggerEnabled && (
          <BoxForm sx={{ ml: CONTAINER_SPACING }}>
            <TextField
              control={control}
              disabled={isDisabled}
              label={t('path.label')}
              name='path'
              placeholder={t('path.placeholder')}
              tooltip={{
                content: t('path.tooltip'),
              }}
            />

            <RadioGroup
              control={control}
              disabled={isDisabled}
              label={t('matchType.name')}
              name='matchType'
              options={matchTypeOptions}
              tooltip={matchTypeTooltip}
            />
          </BoxForm>
        )}
      </BoxForm>

      <GapBox />

      <BoxForm>
        <Switch
          control={control}
          disabled={isDisabled}
          label={t('customVariableTriggerEnabled.label')}
          labelPlacement='start'
          name='customVariableTriggerEnabled'
          tooltip={{
            content: t('customVariableTriggerEnabled.tooltip'),
          }}
        />

        {customVariableTriggerEnabled && (
          <BoxForm sx={{ ml: CONTAINER_SPACING }}>
            <TextField
              control={control}
              disabled={isDisabled}
              label={t('customVariableName.label')}
              name='customVariableName'
              placeholder={t('customVariableName.placeholder')}
            />

            <TextField
              control={control}
              disabled={isDisabled}
              label={t('customVariableValue.label')}
              name='customVariableValue'
              placeholder={t('customVariableValue.placeholder')}
            />
          </BoxForm>
        )}
      </BoxForm>
    </Paper>
  )
}
